import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { usePricesList } from "./use-prices-list";
import { useProductsList } from "./use-products-list";
import { CatalogStatusBadge } from "./CatalogStatusBadge";
import { CreatePriceDialog } from "./CreatePriceDialog";
import { ArchivePriceDialog } from "./ArchivePriceDialog";
import { formatMoney } from "./format-money";
import type { StripePrice } from "./types";

interface PricesSectionProps {
  readonly canEdit: boolean;
}

export function PricesSection({ canEdit }: PricesSectionProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePricesList();
  const { data: products } = useProductsList();
  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<StripePrice | null>(null);

  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    products?.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [products]);

  return (
    <section data-testid="catalog-prices-section" aria-labelledby="catalog-prices-heading">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="catalog-prices-heading" className="font-display text-xl">
          {t("catalog.prices.title")}
        </h2>
        {canEdit && (
          <Button data-testid="create-price-button" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("catalog.prices.actions.create")}
          </Button>
        )}
      </div>

      {isLoading && (
        <p data-testid="catalog-prices-loading" className="font-sans text-sm text-muted-foreground">
          {t("catalog.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="catalog-prices-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("catalog.prices.error")}
          </p>
        </div>
      )}

      {data && data.length === 0 && (
        <div
          data-testid="catalog-prices-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("catalog.prices.emptyTitle")}
          </p>
          <p className="mt-1 font-sans text-sm font-normal text-muted-foreground">
            {t("catalog.prices.emptyBody")}
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div data-testid="catalog-prices-list" className="space-y-3">
          {data.map((price) => {
            const productName = productNameById.get(price.productId) ?? price.productId;
            return (
              <article
                key={price.id}
                data-testid={`price-row-${price.id}`}
                className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-card p-4"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-sans text-base font-medium text-foreground">
                      {formatMoney(price.unitAmount, price.currency)}
                    </h3>
                    <span
                      data-testid={`price-interval-${price.id}`}
                      className="font-sans text-sm text-muted-foreground"
                    >
                      {price.recurring
                        ? t(`catalog.prices.recurring.${price.recurring.intervalUnit}`, {
                            count: price.recurring.intervalCount,
                          })
                        : t("catalog.prices.recurring.oneTime")}
                    </span>
                    <CatalogStatusBadge active={price.active} testId={`price-status-${price.id}`} />
                  </div>
                  <p className="font-sans text-sm text-muted-foreground">
                    {t("catalog.prices.fields.product")}: {productName}
                  </p>
                  {price.lookupKey && (
                    <p className="font-mono text-xs text-muted-foreground">
                      {t("catalog.prices.fields.lookupKey")}: {price.lookupKey}
                    </p>
                  )}
                  <p className="font-mono text-xs text-muted-foreground">{price.id}</p>
                </div>
                {canEdit && price.active && (
                  <Button
                    data-testid={`archive-price-${price.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => setArchiveTarget(price)}
                  >
                    {t("catalog.prices.actions.archive")}
                  </Button>
                )}
              </article>
            );
          })}
        </div>
      )}

      <CreatePriceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setCreateOpen(false)}
      />
      <ArchivePriceDialog
        price={archiveTarget}
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
        onSuccess={() => setArchiveTarget(null)}
      />
    </section>
  );
}
